import React, { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import genericUtility from "../../../services/utility/GenericUtility";
import { openAuth } from "../../../dataManagement/uiDb/GeneralDb";
import { fetchProfile } from "../../../services/thunks/ProfileThunk";
import { fetchBusinessById } from "../../../services/thunks/BusinessThunk";
import DineInThunk from "../../../services/thunks/DineInThunk";
import {
  selectDineInPhase,
  selectDineInLoadingMap,
  selectDineInErrorMap,
  loadMockSession,
} from "../../../dataManagement/uiDb/DineInDb";
import { getStoredDineInCode } from "../../../services/helpers/DineInStorageHelper";
import { buildMockDineInSeed } from "./dineInMockData";
import { useNotificationPermission } from "../../../hooks/useNotificationPermission";
import DineInNotificationGate from "./childComponents/DineInNotificationGate";
import DineInInitialScreen from "./childComponents/DineInInitialScreen";
import DineInSession from "./DineInSession";
import "./styles/DineIn.css";

/**
 * DEV-ONLY escape hatch: /dine-in/:businessId/:tableNumber?mock=1 skips the
 * real openTable call and loads a local fixture straight into DineInDb, for
 * building DineInSession's UI without a live backend. import.meta.env.DEV
 * makes this dead code (and unreachable) in a production build.
 */
const isMockRequested = () =>
  import.meta.env.DEV && new URLSearchParams(window.location.search).get("mock") === "1";

/**
 * Entry point for a table's QR scan: /dine-in/:businessId/:tableNumber.
 * Public route (no RequireAuth) so the deep link survives a guest sign-in —
 * see the guest branch below, which opens the shared Auth modal in place via
 * openAuth(pathname) instead of redirecting to a separate route and losing
 * the table the person actually scanned.
 */
const DineIn: React.FC = () => {
  const { businessId, tableNumber } = useParams<{
    businessId: string;
    tableNumber: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const isGuest = !genericUtility.getCookie("token");
  const { state: notifState } = useNotificationPermission();
  // "unsupported" (browser genuinely can't do push) can't be mandated —
  // block on it and this screen would be a permanent dead end, so it's
  // treated as ready. Every other non-granted state blocks entry.
  const notificationsReady = notifState === "granted" || notifState === "unsupported";
  const phase = useSelector(selectDineInPhase);
  const loadingMap = useSelector(selectDineInLoadingMap);
  const errorMap = useSelector(selectDineInErrorMap);
  const currentBusiness = useSelector((state: RootState) => state.business.currentBusiness);
  const profileData = useSelector((state: RootState) => state.profile.data);

  // This route sits outside LoadUserData (see comment below), so nothing
  // else fetches the profile here — without this, state.profile.data stays
  // null forever and DineInFeedList's "isMine" check can never match.
  useEffect(() => {
    if (!isGuest && !profileData) dispatch(fetchProfile());
  }, [isGuest, profileData, dispatch]);

  const businessName =
    currentBusiness && currentBusiness.businessId === businessId
      ? currentBusiness.name
      : "this table";

  useEffect(() => {
    if (businessId && currentBusiness?.businessId !== businessId) {
      dispatch(fetchBusinessById(businessId));
    }
  }, [dispatch, businessId, currentBusiness?.businessId]);

  useEffect(() => {
    if (isGuest) dispatch(openAuth(window.location.pathname));
  }, [isGuest, dispatch]);

  // Bootstrap call — fires once per mount. Prefers a code carried in the URL
  // (?code=... — see DineInShareHelper's invite link) over whatever's on
  // file in local storage for this table: a just-tapped invite link is a
  // fresher signal than a cached code that could be from a prior, possibly-
  // ended session. Falls back to the stored code, or null the very first
  // time anyone visits with neither.
  const requestedKeyRef = useRef<string | null>(null);
  const runOpenTable = () => {
    if (!businessId || !tableNumber) return;
    const urlCode = searchParams.get("code");
    const storedCode = getStoredDineInCode(businessId, tableNumber);
    dispatch(
      DineInThunk.openTable({ businessId, tableNumber, sessionCode: urlCode || storedCode })
    );
  };
  useEffect(() => {
    if (
      isGuest ||
      !businessId ||
      !tableNumber ||
      !notificationsReady
    ) {
      return;
    }
    const key = `${businessId}_${tableNumber}`;
    if (requestedKeyRef.current === key) return;
    requestedKeyRef.current = key;

    // A code in the URL has done its job once runOpenTable (below) reads it
    // — strip it back out so it doesn't linger visibly in the address bar
    // or browser history.
    if (searchParams.has("code")) {
      const next = new URLSearchParams(searchParams);
      next.delete("code");
      setSearchParams(next, { replace: true });
    }

    if (isMockRequested()) {
      dispatch(loadMockSession(buildMockDineInSeed(businessId, tableNumber)));
      return;
    }
    runOpenTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, businessId, tableNumber, notificationsReady]);

  if (!businessId || !tableNumber) {
    return (
      <div className="dine-in-page dine-in-page--centered">
        <p>This table link looks broken. Try scanning the QR code again.</p>
      </div>
    );
  }

  if (isGuest) {
    return (
      <div className="dine-in-page dine-in-page--centered">
        <p>Sign in to join this table.</p>
      </div>
    );
  }

  // Mandatory gate: explained and resolved BEFORE anything else about this
  // table — including the openTable call itself (see the bootstrap effect's
  // guard above) — since the whole live-sync pipeline depends on it.
  if (!notificationsReady) {
    return (
      <div className="dine-in-page">
        <DineInNotificationGate />
      </div>
    );
  }

  if (phase === "bootstrapping") {
    return (
      <div className="dine-in-page dine-in-page--centered">
        <p>{loadingMap.openTable ?? "Setting up your table..."}</p>
      </div>
    );
  }

  if (phase === "unavailable") {
    return (
      <div className="dine-in-page dine-in-page--centered">
        <p>{errorMap.openTable ?? "Something went wrong. Please try again."}</p>
        <button className="dine-in__retry-btn" onClick={runOpenTable}>
          Try again
        </button>
      </div>
    );
  }

  if (phase === "inSession") {
    return (
      <div className="dine-in-page">
        <DineInSession businessName={businessName} />
      </div>
    );
  }

  // "revealCode" or "needsCode"
  return (
    <div className="dine-in-page">
      <DineInInitialScreen
        businessId={businessId}
        tableNumber={tableNumber}
        businessName={businessName}
        businessLogoUrl={
          currentBusiness?.businessId === businessId ? currentBusiness.logoUrl : undefined
        }
      />
    </div>
  );
};

export default DineIn;
